"use client";
import { useEffect, useRef, useState } from "react";
import {
  usePublicReferralInvite,
  usePublicRevenueImpact,
  trackReferralClick,
} from "../services/referralClient";
import { getStoredReferralAttribution } from "../services/referralStorage";
import { useReferralAnalytics } from "./useReferralAnalytics";
import { buildAppLoginUrl, getPortalBaseUrl } from "@/lib/utils";

export function useReferralInviteTracking(creatorUniqueId: string) {
  const trackedRef = useRef(false);
  const [isStartingSignup, setIsStartingSignup] = useState(false);
  const { data, isLoading, isError } = usePublicReferralInvite(creatorUniqueId);
  const { data: impact } = usePublicRevenueImpact(data?.id);
  const { trackReferAndEarnInvitation } = useReferralAnalytics();

  useEffect(() => {
    if (!isError) return;
    window.location.href = getPortalBaseUrl();
  }, [isError]);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackReferralClick(creatorUniqueId).catch(() => {
      // Signup must keep working even when attribution tracking fails.
    });
  }, [creatorUniqueId]);

  const handleSignupClick = async () => {
    if (isStartingSignup) return;

    trackReferAndEarnInvitation(creatorUniqueId);
    setIsStartingSignup(true);
    try {
      let stored = getStoredReferralAttribution();
      if (!stored || stored.creatorUniqueId !== creatorUniqueId) {
        const click = await trackReferralClick(creatorUniqueId);
        stored = {
          attributionToken: click.attributionToken,
          creatorUniqueId: click.creatorUniqueId,
          expiresAt: click.expiresAt,
        };
      }

      const loginUrl = buildAppLoginUrl({
        referralToken: stored?.attributionToken,
      });
      window.location.href = loginUrl;
    } catch {
      window.location.href = buildAppLoginUrl();
    }
  };

  return {
    data,
    impact,
    isLoading,
    isError,
    isStartingSignup,
    handleSignupClick,
  };
}
